import React, { Fragment, useRef, useEffect, useState } from 'react';
import CheckoutStep from '../CheckoutStep';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import MetaTag from '../../../layout/MetaTag';
import swal from 'sweetalert';
import '../payments.css';
import { createOrderAction, clearErrorAction } from '../../../redux/actions/orderAction';

function PaymentForBuyDirect() {
    const dispatch = useDispatch();
    const history = useHistory();

    const paymentAmount = JSON.parse(sessionStorage.getItem('orderInfo') || '{}');
    const productBuy = JSON.parse(localStorage.getItem('productBuy') || '{}');

    const productAmountWithGst = {
        roundOffTotalTax: paymentAmount?.roundOffTotalTax || 0,
        shippingCharges: paymentAmount?.shippingCharges || 0,
        boxCharges: paymentAmount?.boxCharges || 0,
        itemPrice: paymentAmount?.itemPrice || 0,
        subTotal: paymentAmount?.subTotal || 0,
        totalPrice: paymentAmount?.totalPrice || 0,
        coupanCode: paymentAmount?.coupanCode,
        coupanValue: paymentAmount?.coupanValue || 0,
    };

    const finalAmount = {
        orderItems: productBuy,
        coupanCode: paymentAmount?.coupanCode,
    };

    const { shippingInfo } = useSelector((state) => state.shippingInfo);
    const { user } = useSelector((state) => state.user);
    const { error } = useSelector((state) => state.newOrder);

    const payBtn = useRef(null);

    // Selected payment method state: 'COD' | 'ADVANCE_COD' | 'FULL_ONLINE'
    const [selectedMethod, setSelectedMethod] = useState('COD');
    const [advacePayment, setAdvacePayment] = useState('25');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const totalPayable = Number(productAmountWithGst.totalPrice || 0);
    const advancePercentage = Number(advacePayment || 0);
    const advancePreviewAmount = Math.round((totalPayable * advancePercentage) / 100);
    const balanceOnDelivery = Math.max(totalPayable - advancePreviewAmount, 0);

    const address = `${shippingInfo.address}, ${shippingInfo.landMark ? `${shippingInfo.landMark}, ` : ''}${shippingInfo.city}, ${shippingInfo.state}, ${shippingInfo.pincode}`;

    const order = {
        userEmail: user?.email,
        userName: user?.firstName,
        userNumber: user?.number,
        shippingInfo: shippingInfo,
        orderItems: [productBuy],
        itemPrice: productAmountWithGst.itemPrice,
        subTotal: productAmountWithGst.subTotal,
        taxPrice: productAmountWithGst.roundOffTotalTax,
        shoesPackingDetails: productAmountWithGst.boxCharges,
        shippingPrice: productAmountWithGst.shippingCharges,
        totalPrice: productAmountWithGst.totalPrice,
        coupanCode: productAmountWithGst.coupanCode,
        coupanValue: productAmountWithGst.coupanValue,
    };

    const getPaymentErrorMessage = (err) =>
        err.response && err.response.data && err.response.data.msg
            ? err.response.data.msg
            : err.message || 'Payment processing failed. Please try again.';

    // Load Cashfree script
    const loadScript = (src) => {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = src;
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const openCashfreeCheckout = async (paymentSessionId, mode) => {
        const res = await loadScript('https://sdk.cashfree.com/js/v3/cashfree.js');
        if (!res || !window.Cashfree) {
            throw new Error('Cashfree checkout could not be loaded. Please try again.');
        }

        const cashfree = window.Cashfree({
            mode: mode === 'production' ? 'production' : 'sandbox',
        });

        await cashfree.checkout({
            paymentSessionId,
            redirectTarget: '_modal',
        });
    };

    // 1. Full Online Payment Handler
    const handleFullOnlinePayment = async () => {
        setIsSubmitting(true);
        try {
            const { data } = await axios.post(`/api/payment/confirm`, finalAmount);
            await openCashfreeCheckout(data.paymentSessionId, data.mode);

            const result = await axios.post(`/api/payment/status`, {
                orderId: data.orderId,
            });

            if (result.data.isPaid === true) {
                order.paymentMode = 'FULL_ONLINE';
                order.remainingAmount = 0;
                order.paymentInfo = {
                    id: data.orderId,
                    status: result.data.msg || 'Full Online Payment Received',
                };
                dispatch(createOrderAction(order));
                history.push('/payment/successfull');
            } else {
                swal({
                    title: 'Payment Incomplete',
                    text: 'Payment has not been confirmed yet. Please complete the transaction to place your order.',
                    icon: 'info',
                });
            }
        } catch (err) {
            swal({
                title: 'Payment Error',
                text: getPaymentErrorMessage(err),
                icon: 'error',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // 2. Advance COD Payment Handler
    const handleAdvancePayment = async () => {
        if (!advacePayment || Number(advacePayment) <= 0) {
            return swal({
                title: 'Select Advance Amount',
                text: 'Please select what percentage of advance you want to pay.',
                icon: 'info',
            });
        }

        setIsSubmitting(true);
        try {
            const { data } = await axios.post(`/api/advance-payment/confirm`, {
                orderItems: productBuy,
                coupanCode: paymentAmount?.coupanCode,
                advacePayment,
            });

            await openCashfreeCheckout(data.paymentSessionId, data.mode);

            const result = await axios.post(`/api/payment/status`, {
                orderId: data.orderId,
            });

            if (result.data.isPaid === true) {
                const paidAdvanceAmount = Number(data.msg || advancePreviewAmount);
                order.paymentMode = 'ADVANCE_COD';
                order.remainingAmount = Math.max(totalPayable - paidAdvanceAmount, 0);
                order.adavncePayment = {
                    id: data.orderId,
                    status: result.data.msg || 'Advance Paid',
                    advanceAmountPercentage: Number(advacePayment),
                    amount: paidAdvanceAmount,
                    remainingAmount: Math.max(totalPayable - paidAdvanceAmount, 0),
                };
                dispatch(createOrderAction(order));
                history.push('/payment/successfull');
            } else {
                swal({
                    title: 'Advance Payment Incomplete',
                    text: 'Payment has not been confirmed yet. Please complete payment to place your order.',
                    icon: 'info',
                });
            }
        } catch (err) {
            swal({
                title: 'Payment Error',
                text: getPaymentErrorMessage(err),
                icon: 'error',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // COD Email OTP Verification State
    const [showCodOtpModal, setShowCodOtpModal] = useState(false);
    const [codOtpInput, setCodOtpInput] = useState('');
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [resendTimer, setResendTimer] = useState(0);

    // Countdown for OTP resend
    useEffect(() => {
        let interval = null;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimer]);

    // Send COD Email OTP Handler
    const handleRequestCodOtp = async () => {
        const targetEmail = order.userEmail || user?.email || shippingInfo?.email;
        if (!targetEmail) {
            swal({
                title: 'Email Required',
                text: 'Please provide a valid email in your profile or shipping details to receive your verification code.',
                icon: 'warning',
            });
            return;
        }

        setIsSendingOtp(true);
        setOtpError('');
        try {
            await axios.post('/api/order/cod/send-email-otp', { email: targetEmail });
            setShowCodOtpModal(true);
            setResendTimer(30);
        } catch (err) {
            swal({
                title: 'OTP Error',
                text: err.response?.data?.msg || err.message || 'Failed to dispatch verification code.',
                icon: 'error',
            });
        } finally {
            setIsSendingOtp(false);
        }
    };

    // Verify COD Email OTP & Place Order
    const handleVerifyCodOtpAndPlaceOrder = async () => {
        const targetEmail = order.userEmail || user?.email || shippingInfo?.email;
        if (!codOtpInput || codOtpInput.trim().length !== 6) {
            setOtpError('Please enter a valid 6-digit verification code.');
            return;
        }

        setIsVerifyingOtp(true);
        setOtpError('');
        try {
            await axios.post('/api/order/cod/verify-email-otp', {
                email: targetEmail,
                otp: codOtpInput.trim(),
            });

            // Finalize order creation with verified COD
            order.paymentMode = 'COD';
            order.codOtp = codOtpInput.trim();
            order.remainingAmount = totalPayable;
            order.paymentInfo = {
                id: 'COD',
                status: 'Cash On Delivery (Pay upon delivery)',
            };
            order.adavncePayment = {
                id: 'COD',
                status: 'Pending Cash on Delivery',
                advanceAmountPercentage: 0,
                amount: 0,
                remainingAmount: totalPayable,
            };

            dispatch(createOrderAction(order));
            setShowCodOtpModal(false);
            history.push('/cashonDelivery/payment/successfull');
        } catch (err) {
            setOtpError(err.response?.data?.msg || err.message || 'Invalid or expired verification code.');
        } finally {
            setIsVerifyingOtp(false);
        }
    };

    const handleFinalSubmit = () => {
        if (selectedMethod === 'COD') {
            handleRequestCodOtp();
        } else if (selectedMethod === 'ADVANCE_COD') {
            handleAdvancePayment();
        } else if (selectedMethod === 'FULL_ONLINE') {
            handleFullOnlinePayment();
        }
    };

    useEffect(() => {
        if (error) {
            swal({
                title: 'Order Error',
                text: error,
                icon: 'error',
            });
            dispatch(clearErrorAction());
        }
    }, [dispatch, error]);

    return (
        <Fragment>
            <MetaTag title="Choose Payment Method | IM Online Shopping" />
            <CheckoutStep activeStep={2} />

            <div className="payment-checkout-page">
                {/* LEFT COLUMN: Delivery Address & Payment Option Selector */}
                <div className="payment-left-column">
                    <div className="payment-address-card">
                        <div className="address-card-header">
                            <div className="address-icon-wrap">
                                <LocationOnOutlinedIcon />
                            </div>
                            <div className="address-header-text">
                                <span className="sub-title">DELIVERING TO</span>
                                <strong className="user-name">{shippingInfo.name} • {shippingInfo.phoneNo}</strong>
                                <p className="address-desc">{address}</p>
                            </div>
                            <Link to="/confirm/orders/directbuy" className="change-address-link">
                                Review Items
                            </Link>
                        </div>
                    </div>

                    <div className="payment-methods-card">
                        <div className="methods-card-header">
                            <div className="method-icon-wrap">
                                <AccountBalanceWalletOutlinedIcon />
                            </div>
                            <div>
                                <h3>Select Payment Method</h3>
                                <p>Choose how you would like to complete payment for your direct purchase</p>
                            </div>
                        </div>

                        <div className="methods-list">
                            {/* Option 1: 100% Cash on Delivery */}
                            <div
                                className={`payment-method-item ${selectedMethod === 'COD' ? 'selected' : ''}`}
                                onClick={() => setSelectedMethod('COD')}
                            >
                                <div className="method-radio">
                                    {selectedMethod === 'COD' ? (
                                        <CheckCircleIcon className="radio-active-icon" />
                                    ) : (
                                        <RadioButtonUncheckedIcon className="radio-inactive-icon" />
                                    )}
                                </div>
                                <div className="method-content">
                                    <div className="method-top-row">
                                        <div className="method-title-wrap">
                                            <LocalShippingOutlinedIcon className="method-type-icon" />
                                            <strong>Cash on Delivery (COD)</strong>
                                        </div>
                                        <span className="method-badge cod-badge">NO ADVANCE NEEDED</span>
                                    </div>
                                    <p className="method-desc">
                                        Pay full ₹{totalPayable} with cash or UPI to the courier agent when your package arrives at your doorstep.
                                    </p>

                                    {selectedMethod === 'COD' && (
                                        <div className="method-active-details">
                                            <div className="active-detail-row">
                                                <span>Amount to pay now:</span>
                                                <strong className="text-free">₹0 (Zero)</strong>
                                            </div>
                                            <div className="active-detail-row">
                                                <span>Payable at delivery:</span>
                                                <strong>₹{totalPayable}</strong>
                                            </div>
                                            <span className="delivery-note">✓ Verified Doorstep Handover Guarantee</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Option 2: Pay Advance (Partial COD) */}
                            <div
                                className={`payment-method-item ${selectedMethod === 'ADVANCE_COD' ? 'selected' : ''}`}
                                onClick={() => setSelectedMethod('ADVANCE_COD')}
                            >
                                <div className="method-radio">
                                    {selectedMethod === 'ADVANCE_COD' ? (
                                        <CheckCircleIcon className="radio-active-icon" />
                                    ) : (
                                        <RadioButtonUncheckedIcon className="radio-inactive-icon" />
                                    )}
                                </div>
                                <div className="method-content">
                                    <div className="method-top-row">
                                        <div className="method-title-wrap">
                                            <HandshakeOutlinedIcon className="method-type-icon" />
                                            <strong>Pay Advance &amp; Balance on Delivery</strong>
                                        </div>
                                        <span className="method-badge advance-badge">POPULAR</span>
                                    </div>
                                    <p className="method-desc">
                                        Pay a small advance deposit online now to reserve your pair with priority, and pay the remaining balance in cash on delivery.
                                    </p>

                                    {selectedMethod === 'ADVANCE_COD' && (
                                        <div className="method-active-details">
                                            <div className="advance-selector-wrap">
                                                <label htmlFor="advance-select-db">Choose advance deposit percentage:</label>
                                                <select
                                                    id="advance-select-db"
                                                    value={advacePayment}
                                                    onChange={(e) => setAdvacePayment(e.target.value)}
                                                    className="advance-select-box"
                                                >
                                                    <option value="10">Pay 10% Advance</option>
                                                    <option value="25">Pay 25% Advance (Recommended)</option>
                                                    <option value="50">Pay 50% Advance</option>
                                                    <option value="75">Pay 75% Advance</option>
                                                    <option value="90">Pay 90% Advance</option>
                                                </select>
                                            </div>

                                            <div className="active-detail-row">
                                                <span>Advance to pay now online:</span>
                                                <strong className="text-highlight">₹{advancePreviewAmount}</strong>
                                            </div>
                                            <div className="active-detail-row">
                                                <span>Remaining balance on delivery:</span>
                                                <strong>₹{balanceOnDelivery}</strong>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Option 3: Full Online Payment */}
                            <div
                                className={`payment-method-item ${selectedMethod === 'FULL_ONLINE' ? 'selected' : ''}`}
                                onClick={() => setSelectedMethod('FULL_ONLINE')}
                            >
                                <div className="method-radio">
                                    {selectedMethod === 'FULL_ONLINE' ? (
                                        <CheckCircleIcon className="radio-active-icon" />
                                    ) : (
                                        <RadioButtonUncheckedIcon className="radio-inactive-icon" />
                                    )}
                                </div>
                                <div className="method-content">
                                    <div className="method-top-row">
                                        <div className="method-title-wrap">
                                            <CreditCardOutlinedIcon className="method-type-icon" />
                                            <strong>Full Online Payment</strong>
                                        </div>
                                        <span className="method-badge online-badge">FASTEST DISPATCH</span>
                                    </div>
                                    <p className="method-desc">
                                        Pay 100% full amount now via Cashfree. Supports UPI (Google Pay, PhonePe, Paytm), Credit/Debit Cards, and NetBanking.
                                    </p>

                                    {selectedMethod === 'FULL_ONLINE' && (
                                        <div className="method-active-details">
                                            <div className="active-detail-row">
                                                <span>Full amount to pay now:</span>
                                                <strong className="text-highlight">₹{totalPayable}</strong>
                                            </div>
                                            <div className="active-detail-row">
                                                <span>Payable at delivery:</span>
                                                <strong className="text-free">₹0 (Zero)</strong>
                                            </div>
                                            <span className="delivery-note">✓ 100% Encrypted &amp; PCI-DSS Level 1 Compliant</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT COLUMN: Active Selection Review & Final CTA */}
                <div className="payment-right-column">
                    <div className="payment-summary-card">
                        <div className="summary-header">
                            <h3>Payment Breakdown</h3>
                            <span className="summary-badge">FINAL STEP</span>
                        </div>

                        <div className="selected-mode-banner">
                            <span className="mode-label">SELECTED PAYMENT MODE</span>
                            <div className="mode-title-row">
                                <CheckCircleIcon style={{ fontSize: '18px', color: '#16a34a' }} />
                                <strong>
                                    {selectedMethod === 'COD' && 'Cash on Delivery (100% COD)'}
                                    {selectedMethod === 'ADVANCE_COD' && `Partial COD (${advacePayment}% Advance)`}
                                    {selectedMethod === 'FULL_ONLINE' && 'Full Online Payment'}
                                </strong>
                            </div>
                        </div>

                        <div className="summary-details-list">
                            <div className="summary-item">
                                <span>Items Subtotal</span>
                                <strong>₹{productAmountWithGst.subTotal}</strong>
                            </div>

                            <div className="summary-item">
                                <span>Shipping Fee</span>
                                {productAmountWithGst.shippingCharges === 0 ? (
                                    <strong className="free-text">FREE</strong>
                                ) : (
                                    <strong>₹{productAmountWithGst.shippingCharges}</strong>
                                )}
                            </div>

                            {productAmountWithGst.boxCharges > 0 && (
                                <div className="summary-item">
                                    <span>Packaging Box</span>
                                    <strong>₹{productAmountWithGst.boxCharges}</strong>
                                </div>
                            )}

                            {productAmountWithGst.roundOffTotalTax > 0 && (
                                <div className="summary-item">
                                    <span>GST Tax</span>
                                    <strong>₹{productAmountWithGst.roundOffTotalTax}</strong>
                                </div>
                            )}

                            {productAmountWithGst.coupanValue > 0 && (
                                <div className="summary-item discount">
                                    <span>Coupon Discount ({productAmountWithGst.coupanCode})</span>
                                    <strong>- ₹{productAmountWithGst.coupanValue}</strong>
                                </div>
                            )}
                        </div>

                        <div className="total-due-card">
                            <div className="due-row">
                                <span>Total Order Value</span>
                                <strong className="big-total">₹{totalPayable}</strong>
                            </div>

                            {selectedMethod === 'ADVANCE_COD' && (
                                <div className="advance-split-box">
                                    <div className="split-row">
                                        <span>Amount Due Now:</span>
                                        <strong>₹{advancePreviewAmount}</strong>
                                    </div>
                                    <div className="split-row">
                                        <span>Balance on Delivery:</span>
                                        <strong>₹{balanceOnDelivery}</strong>
                                    </div>
                                </div>
                            )}

                            {selectedMethod === 'COD' && (
                                <div className="advance-split-box">
                                    <div className="split-row">
                                        <span>Amount Due Now:</span>
                                        <strong className="free-text">₹0</strong>
                                    </div>
                                    <div className="split-row">
                                        <span>Cash Due at Doorstep:</span>
                                        <strong>₹{totalPayable}</strong>
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            ref={payBtn}
                            onClick={handleFinalSubmit}
                            disabled={isSubmitting}
                            className="final-place-order-btn"
                        >
                            {isSubmitting ? (
                                'Processing Order...'
                            ) : selectedMethod === 'COD' ? (
                                `Confirm & Place Order (Pay ₹${totalPayable} on Delivery)`
                            ) : selectedMethod === 'ADVANCE_COD' ? (
                                `Pay ₹${advancePreviewAmount} Advance & Place Order`
                            ) : (
                                `Pay ₹${totalPayable} Full Amount Online`
                            )}
                        </button>

                        <div className="payment-security-footer">
                            <VerifiedUserOutlinedIcon style={{ fontSize: '18px', color: '#16a34a' }} />
                            <span>100% Safe Payments • Buyer Protection Guarantee</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Cash on Delivery Email OTP Verification Modal */}
            {showCodOtpModal && (
                <div className="cod-otp-modal-backdrop" onClick={() => setShowCodOtpModal(false)}>
                    <div className="cod-otp-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="cod-modal-header">
                            <div className="cod-modal-icon">🔐</div>
                            <h3>Verify Cash on Delivery Order</h3>
                            <p>
                                For your security, a 6-digit verification code has been sent to{' '}
                                <strong>{order.userEmail || user?.email || shippingInfo?.email}</strong>.
                            </p>
                        </div>

                        <div className="cod-modal-body">
                            <label className="cod-otp-label">Enter 6-Digit Verification Code</label>
                            <input
                                type="text"
                                maxLength="6"
                                className="cod-otp-input"
                                placeholder="• • • • • •"
                                value={codOtpInput}
                                onChange={(e) => {
                                    setCodOtpInput(e.target.value.replace(/\D/g, ''));
                                    setOtpError('');
                                }}
                                autoFocus
                            />

                            {otpError && <div className="cod-otp-error">{otpError}</div>}

                            <div className="cod-resend-row">
                                <span>Didn't receive the email?</span>
                                {resendTimer > 0 ? (
                                    <span className="resend-timer-text">Resend in {resendTimer}s</span>
                                ) : (
                                    <button
                                        type="button"
                                        className="resend-code-btn"
                                        onClick={handleRequestCodOtp}
                                        disabled={isSendingOtp}
                                    >
                                        {isSendingOtp ? 'Sending...' : 'Resend Code'}
                                    </button>
                                )}
                            </div>

                            <div className="cod-modal-actions">
                                <button
                                    type="button"
                                    className="cod-cancel-btn"
                                    onClick={() => setShowCodOtpModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="cod-confirm-btn"
                                    onClick={handleVerifyCodOtpAndPlaceOrder}
                                    disabled={isVerifyingOtp || codOtpInput.length !== 6}
                                >
                                    {isVerifyingOtp ? 'Verifying...' : 'Verify & Confirm Order'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
}

export default PaymentForBuyDirect;
